import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useToast } from '../../App';
import './AISettings.scss';

const AISettings = () => {
    const { showSuccess, showError } = useToast();

    const [providers, setProviders] = useState({});
    const [settings, setSettings] = useState({
        enabled: false,
        provider: 'gemini',
        api_key: '',
        model: 'gemini-2.5-flash-lite',
        temperature: 0.7,
        max_tokens: 1000,
    });
    const [apiKeyInput, setApiKeyInput] = useState(''); // separate so we can show masked value
    const [maskedKey, setMaskedKey] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // { success, message }
    const [showKey, setShowKey] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [provRes, settRes] = await Promise.all([
                api.getAIProviders(),
                api.getAISettings(),
            ]);
            if (provRes.success) setProviders(provRes.providers);
            if (settRes.success) {
                const s = settRes.settings;
                setSettings({
                    enabled:     s.enabled     ?? false,
                    provider:    s.provider    ?? 'gemini',
                    model:       s.model       ?? 'gemini-2.5-flash-lite',
                    temperature: s.temperature ?? 0.7,
                    max_tokens:  s.max_tokens  ?? 1000,
                });
                setMaskedKey(s.api_key_masked ?? '');
            }
        } catch (e) {
            showError('Failed to load AI settings.');
        } finally {
            setLoading(false);
        }
    };

    const currentModels = providers[settings.provider]?.models ?? {};

    const handleProviderChange = (provider) => {
        const firstModel = Object.keys(providers[provider]?.models ?? {})[0] ?? '';
        setSettings(prev => ({ ...prev, provider, model: firstModel }));
        setTestResult(null);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = { ...settings };
            // Only send api_key if user typed a new one
            if (apiKeyInput.trim()) payload.api_key = apiKeyInput.trim();
            const res = await api.saveAISettings(payload);
            if (res.success) {
                showSuccess('AI settings saved.');
                setApiKeyInput('');
                await loadData(); // refresh masked key
            } else {
                showError(res.message || 'Save failed.');
            }
        } catch (e) {
            showError('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const payload = {
                provider: settings.provider,
                model:    settings.model,
            };
            if (apiKeyInput.trim()) payload.api_key = apiKeyInput.trim();
            const res = await api.testAIConnection(payload);
            setTestResult({ success: res.success, message: res.message });
        } catch (e) {
            setTestResult({ success: false, message: 'Request failed.' });
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return <div className="ai-settings-loading">Loading AI settings…</div>;
    }

    return (
        <div className="ai-settings">
            {/* Header */}
            <div className="ai-settings-header">
                <div className="ai-header-icon">🤖</div>
                <div>
                    <h2>AI Integration</h2>
                    <p>Connect an AI provider to generate product content and order notes automatically.</p>
                </div>
            </div>

            {/* Enable toggle */}
            <div className="card ai-card">
                <div className="card-header">
                    <h3>Enable AI Features</h3>
                </div>
                <div className="card-content">
                    <div className="setting-item">
                        <div className="setting-info">
                            <label className="setting-label">AI Integration</label>
                            <p className="setting-description">
                                When enabled, AI buttons appear in the Product Editor and Order Editor.
                            </p>
                        </div>
                        <div className="setting-control">
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={settings.enabled}
                                    onChange={e => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Provider selection */}
            <div className="card ai-card">
                <div className="card-header">
                    <h3>Provider &amp; Model</h3>
                    <p>Choose your AI provider and the model to use.</p>
                </div>
                <div className="card-content">
                    <div className="ai-providers-grid">
                        {Object.entries(providers).map(([key, prov]) => (
                            <button
                                key={key}
                                className={`ai-provider-btn ${settings.provider === key ? 'active' : ''}`}
                                onClick={() => handleProviderChange(key)}
                            >
                                <span className="provider-icon">{providerIcon(key)}</span>
                                <span className="provider-label">{prov.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="form-group mt-4">
                        <label>Model</label>
                        <select
                            value={settings.model}
                            onChange={e => setSettings(prev => ({ ...prev, model: e.target.value }))}
                        >
                            {Object.entries(currentModels).map(([val, label]) => (
                                <option key={val} value={val}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* API Key */}
            <div className="card ai-card">
                <div className="card-header">
                    <h3>API Key</h3>
                    <p>Your key is stored securely in the WordPress database and never exposed in the frontend.</p>
                </div>
                <div className="card-content">
                    <div className="form-group">
                        <label>
                            {providers[settings.provider]?.label ?? 'Provider'} API Key
                        </label>
                        <div className="api-key-input-wrapper">
                            <input
                                type={showKey ? 'text' : 'password'}
                                className="form-control"
                                placeholder={maskedKey ? maskedKey : 'Enter your API key…'}
                                value={apiKeyInput}
                                onChange={e => setApiKeyInput(e.target.value)}
                                autoComplete="off"
                            />
                            <button
                                type="button"
                                className="btn-icon"
                                onClick={() => setShowKey(v => !v)}
                                title={showKey ? 'Hide' : 'Show'}
                            >
                                {showKey ? '🙈' : '👁️'}
                            </button>
                        </div>
                        {maskedKey && !apiKeyInput && (
                            <p className="field-hint">Key saved: {maskedKey} — enter a new key to replace it.</p>
                        )}
                        <p className="field-hint">
                            Get your key from the provider's dashboard.{' '}
                            {settings.provider === 'gemini' && (
                                <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer">
                                    Google AI Studio →
                                </a>
                            )}
                            {settings.provider === 'openai' && (
                                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">
                                    OpenAI Platform →
                                </a>
                            )}
                            {settings.provider === 'claude' && (
                                <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
                                    Anthropic Console →
                                </a>
                            )}
                            {settings.provider === 'deepseek' && (
                                <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noreferrer">
                                    DeepSeek Platform →
                                </a>
                            )}
                            {settings.provider === 'openrouter' && (
                                <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer">
                                    OpenRouter →
                                </a>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            {/* Advanced */}
            <div className="card ai-card">
                <div className="card-header">
                    <h3>Generation Parameters</h3>
                    <p>Fine-tune how the AI generates content.</p>
                </div>
                <div className="card-content ai-params-grid">
                    <div className="form-group">
                        <label>Temperature <span className="param-value">{settings.temperature}</span></label>
                        <input
                            type="range"
                            min="0" max="1" step="0.05"
                            value={settings.temperature}
                            onChange={e => setSettings(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                        />
                        <div className="range-labels">
                            <span>Precise (0)</span>
                            <span>Creative (1)</span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Max Tokens</label>
                        <select
                            value={settings.max_tokens}
                            onChange={e => setSettings(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
                        >
                            <option value={500}>500 — Short</option>
                            <option value={1000}>1000 — Medium (recommended)</option>
                            <option value={2000}>2000 — Long</option>
                            <option value={4000}>4000 — Very Long</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Test connection */}
            <div className="card ai-card">
                <div className="card-header">
                    <h3>Test Connection</h3>
                    <p>Send a quick ping to verify your API key and model work correctly.</p>
                </div>
                <div className="card-content">
                    <button
                        className={`btn btn-secondary ${testing ? 'btn-loading' : ''}`}
                        onClick={handleTest}
                        disabled={testing || (!maskedKey && !apiKeyInput.trim())}
                    >
                        {testing ? <><span className="spinner-sm"></span> Testing…</> : '🔌 Test Connection'}
                    </button>

                    {testResult && (
                        <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                            <span className="test-icon">{testResult.success ? '✅' : '❌'}</span>
                            <span>{testResult.message}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Save */}
            <div className="ai-settings-footer">
                <button
                    className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? <><span className="spinner-sm"></span> Saving…</> : '💾 Save AI Settings'}
                </button>
            </div>
        </div>
    );
};

const providerIcon = (key) => {
    const icons = {
        gemini:     '✨',
        openai:     '🟢',
        claude:     '🟠',
        deepseek:   '🔵',
        openrouter: '🌐',
    };
    return icons[key] ?? '🤖';
};

export default AISettings;
